import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCluster } from '../context/ClusterContext';
import { useAuth } from '../context/AuthContext';
import NavIcon from './sidebarIcons';
import './Sidebar.css';

function Sidebar() {
  const location = useLocation();
  const { clusters, selectedCluster, selectCluster } = useCluster();
  const { isSuperAdmin, isAdmin, hasSection } = useAuth();
  const [expandedSections, setExpandedSections] = useState({
    workloads: true,
    networking: false,
    storage: false,
    configuration: false,
    administration: false,
    compute: location.pathname.startsWith('/compute'),
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const isActive = (path) => location.pathname === path;

  const menuSections = [
    {
      id: 'cluster',
      title: 'Cluster (all resources)',
      icon: 'cluster',
      section: 'clusters',
      items: [
        { name: 'Namespaces', path: '/namespaces' },
        { name: 'Nodes', path: '/nodes' },
        { name: 'Events', path: '/events' },
      ],
    },
    {
      id: 'workloads',
      title: 'Workloads',
      icon: 'workloads',
      section: 'workloads',
      items: [
        { name: 'Pods', path: '/pods' },
        { name: 'Deployments', path: '/deployments' },
        { name: 'StatefulSets', path: '/statefulsets' },
        { name: 'DaemonSets', path: '/daemonsets' },
        { name: 'Jobs', path: '/jobs' },
        { name: 'CronJobs', path: '/cronjobs' },
      ],
    },
    {
      id: 'configuration',
      title: 'Configuration',
      icon: 'configuration',
      section: 'configuration',
      items: [
        { name: 'ConfigMaps', path: '/configmaps' },
        { name: 'Secrets', path: '/secrets' },
        { name: 'HPA', path: '/hpa' },
        { name: 'Resource Quotas', path: '/resource-quotas' },
        { name: 'Limit Ranges', path: '/limit-ranges' },
      ],
    },
    {
      id: 'networking',
      title: 'Network',
      icon: 'network',
      section: 'networking',
      items: [
        { name: 'Services', path: '/services' },
        { name: 'Endpoints', path: '/endpoints' },
        { name: 'Ingresses', path: '/ingresses' },
        { name: 'Network Policies', path: '/network-policies' },
      ],
    },
    {
      id: 'storage',
      title: 'Storage',
      icon: 'storage',
      section: 'storage',
      items: [
        { name: 'Persistent Volume Claims', path: '/pvcs' },
        { name: 'Persistent Volumes', path: '/pvs' },
        { name: 'Storage Classes', path: '/storage-classes' },
      ],
    },
    {
      id: 'access',
      title: 'Access Control',
      icon: 'access',
      section: 'configuration',
      items: [
        { name: 'Service Accounts', path: '/service-accounts' },
        { name: 'Roles', path: '/roles' },
        { name: 'Cluster Roles', path: '/cluster-roles' },
        { name: 'Role Bindings', path: '/role-bindings' },
        { name: 'Cluster Role Bindings', path: '/cluster-role-bindings' },
      ],
    },
  ];

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <Link to="/" className="sidebar-brand">
          SarvKube
        </Link>
        <div className="sidebar-subtitle">Cluster Management</div>
      </div>

      {/* Cluster selector */}
      <div className="sidebar-cluster-selector">
        <div className="sidebar-cluster-label">Active Cluster</div>
        <select
          className="sidebar-cluster-select"
          value={selectedCluster?.id || ''}
          onChange={e => {
            const c = clusters.find(x => String(x.id) === e.target.value);
            if (c) selectCluster(c);
          }}
        >
          {clusters.length === 0 && <option value="">No clusters</option>}
          {clusters.map(c => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.environment})
            </option>
          ))}
        </select>
        {selectedCluster && (
          <div className={`sidebar-cluster-status sidebar-cluster-status-${selectedCluster.status}`}>
            {selectedCluster.status}
          </div>
        )}
      </div>

      <nav className="sidebar-nav">
        <div className="sidebar-section">
          <Link to="/" className={`sidebar-item ${isActive('/') ? 'active' : ''}`}>
            <NavIcon name="dashboard" />
            <span>Dashboard</span>
          </Link>

          {hasSection('servers') && (
            <Link to="/servers" className={`sidebar-item ${isActive('/servers') ? 'active' : ''}`}>
              <NavIcon name="servers" />
              <span>Servers</span>
            </Link>
          )}

          {hasSection('clusters') && (
            <Link to="/clusters" className={`sidebar-item ${isActive('/clusters') ? 'active' : ''}`}>
              <NavIcon name="clusters" />
              <span>Manage Clusters</span>
            </Link>
          )}

          <Link to="/catalog" className={`sidebar-item ${isActive('/catalog') ? 'active' : ''}`}>
            <NavIcon name="compute" />
            <span>App Catalog</span>
          </Link>

          {hasSection('alerts') && (
            <>
              <Link to="/alerts" className={`sidebar-item ${isActive('/alerts') ? 'active' : ''}`}>
                <NavIcon name="alerts" />
                <span>Alerts</span>
              </Link>
              <Link to="/alerts/search" className={`sidebar-item ${isActive('/alerts/search') ? 'active' : ''}`}>
                <NavIcon name="alert-search" />
                <span>Alert Search</span>
              </Link>
            </>
          )}

          {hasSection('playbooks') && (
            <Link to="/playbooks" className={`sidebar-item ${isActive('/playbooks') ? 'active' : ''}`}>
              <NavIcon name="playbooks" />
              <span>Playbooks</span>
            </Link>
          )}

          {hasSection('metrics') && (
            <Link to="/metrics" className={`sidebar-item ${isActive('/metrics') ? 'active' : ''}`}>
              <NavIcon name="metrics" />
              <span>Metrics</span>
            </Link>
          )}

          {hasSection('clusters') && (
            <Link to="/events" className={`sidebar-item ${isActive('/events') ? 'active' : ''}`}>
              <NavIcon name="events" />
              <span>Events</span>
            </Link>
          )}

          {hasSection('gpu') && (
            <Link to="/gpu" className={`sidebar-item ${isActive('/gpu') ? 'active' : ''}`}>
              <NavIcon name="gpu" />
              <span>GPU Servers</span>
            </Link>
          )}

          {hasSection('workloads') && (
            <div className="sidebar-section" style={{ marginBottom: 0 }}>
              <button
                className="sidebar-section-header"
                onClick={() => toggleSection('compute')}
              >
                <NavIcon name="compute" />
                <span className="sidebar-section-title">My Deployments</span>
                <span className={`sidebar-chevron ${expandedSections.compute ? 'expanded' : ''}`}>›</span>
              </button>
              {expandedSections.compute && (
                <div className="sidebar-section-content">
                  {/* "My Pods" (a standalone list of bare-Pod deploys, kind==='Pod') was
                      folded into My Workloads as regular rows — it was a near-empty,
                      rarely-used page on its own. /compute/instances/:id (detail) and
                      /compute/instances/new (launch a bare Pod) still exist and still
                      work; only the separate LIST page is gone. */}
                  <Link to="/compute/workloads"
                    className={`sidebar-item ${location.pathname.startsWith('/compute/workloads') || location.pathname.startsWith('/compute/instances') ? 'active' : ''}`}>
                    <NavIcon name="workloads" />
                    <span>My Workloads</span>
                  </Link>
                  <Link to="/compute/services"
                    className={`sidebar-item ${location.pathname.startsWith('/compute/services') ? 'active' : ''}`}>
                    <NavIcon name="instances" />
                    <span>My Services</span>
                  </Link>
                  <Link to="/compute/images"
                    className={`sidebar-item ${location.pathname.startsWith('/compute/images') || location.pathname.startsWith('/compute/build') ? 'active' : ''}`}>
                    <NavIcon name="storage" />
                    <span>Images</span>
                  </Link>
                </div>
              )}
            </div>
          )}

          {hasSection('vpn') && (
            <Link to="/vpn-profiles" className={`sidebar-item ${isActive('/vpn-profiles') ? 'active' : ''}`}>
              <NavIcon name="vpn" />
              <span>VPN Profiles</span>
            </Link>
          )}
        </div>

        <div className="sidebar-divider"></div>

        {menuSections
          .filter(section => hasSection(section.section))
          .map((section) => (
            <div key={section.id} className="sidebar-section">
              <button
                className="sidebar-section-header"
                onClick={() => toggleSection(section.id)}
              >
                <NavIcon name={section.icon} />
                <span className="sidebar-section-title">{section.title}</span>
                <span className={`sidebar-chevron ${expandedSections[section.id] ? 'expanded' : ''}`}>
                  ›
                </span>
              </button>
              {expandedSections[section.id] && (
                <div className="sidebar-section-content">
                  {section.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`sidebar-subitem ${isActive(item.path) ? 'active' : ''}`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))
        }
      </nav>

      <div className="sidebar-footer">
        {isSuperAdmin && (
          <Link to="/admin/orgs" className={`sidebar-item ${isActive('/admin/orgs') ? 'active' : ''}`}>
            <NavIcon name="orgs" />
            <span>Organizations</span>
          </Link>
        )}
        {isAdmin && (
          <Link to="/admin/users" className={`sidebar-item ${isActive('/admin/users') ? 'active' : ''}`}>
            <NavIcon name="users" />
            <span>User Management</span>
          </Link>
        )}
        {isAdmin && (
          <Link to="/admin/groups" className={`sidebar-item ${isActive('/admin/groups') ? 'active' : ''}`}>
            <NavIcon name="groups" />
            <span>Groups</span>
          </Link>
        )}
        {/* Not admin-gated: non-admins need this page to link their own Rancher account,
            which is the only way SarvKube can show them their namespaces. The page hides
            the management controls from them. */}
        <Link to="/admin/namespace-access" className={`sidebar-item ${isActive('/admin/namespace-access') ? 'active' : ''}`}>
          <NavIcon name="groups" />
          <span>Namespace Access</span>
        </Link>
        <Link to="/settings" className={`sidebar-item ${isActive('/settings') ? 'active' : ''}`}>
          <NavIcon name="settings" />
          <span>Settings</span>
        </Link>
      </div>
    </aside>
  );
}

export default Sidebar;
